/**
 * Persist meal logs and meal history for a specific child (localStorage).
 * Keyed by child ID + date (YYYY-MM-DD) so logs are per child per day.
 * Meal history is also per child.
 */

import type { DailyMealLog, MealLogEntry, MealSlotId, ExtraSnackLog, HydrationLogEntry, DailyTrainingFeedback, TrainingFeedbackEntry, SavedRecipe, FoodLogEntry, ExtractedIngredient, MealTag } from '../types'
import { syncMealLog, syncFeedback } from '../services/syncService'

const MAX_HISTORY = 100

// Per-child storage key helpers
const k = {
  logs: (childId: string) => `energymatch-daily-logs-${childId}`,
  extraSnacks: (childId: string) => `energymatch-extra-snacks-${childId}`,
  hydration: (childId: string) => `energymatch-hydration-${childId}`,
  feedback: (childId: string) => `energymatch-training-feedback-${childId}`,
  history: (childId: string) => `energymatch-meal-history-${childId}`,
  historyBySlot: (childId: string) => `energymatch-meal-history-by-slot-v1-${childId}`,
  recipes: (childId: string) => `energymatch-saved-recipes-${childId}`,
  recipeUsage: (childId: string) => `energymatch-recipe-usage-${childId}`,
}

function getStoredLogs(childId: string): Record<string, DailyMealLog> {
  try {
    const raw = localStorage.getItem(k.logs(childId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, DailyMealLog>
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function setStoredLogs(childId: string, logs: Record<string, DailyMealLog>): void {
  try {
    localStorage.setItem(k.logs(childId), JSON.stringify(logs))
  } catch {
    // quota or disabled
  }
}

/** Get today's date as YYYY-MM-DD in local timezone. */
export function getTodayDateKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** All date keys that have meal log or training feedback data for a child, newest first. */
export function getDateKeysWithData(childId: string): string[] {
  const logKeys = Object.keys(getStoredLogs(childId))
  const feedbackData = getStoredTrainingFeedback(childId)
  const feedbackKeys = Object.keys(feedbackData)
  const set = new Set<string>([...logKeys, ...feedbackKeys])
  return Array.from(set).sort().reverse()
}

/** Get meal log for a given child + date. */
export function getDailyLog(childId: string, dateKey: string): DailyMealLog {
  const logs = getStoredLogs(childId)
  return logs[dateKey] ?? {}
}

/** Save one slot's log for a child + date; merges with existing. */
export function setLogEntry(
  userId: string,
  childId: string,
  dateKey: string,
  slotId: MealSlotId,
  entry: MealLogEntry | null
): void {
  const logs = getStoredLogs(childId)
  const day = logs[dateKey] ?? {}
  if (entry === null) {
    const { [slotId]: _, ...rest } = day
    if (Object.keys(rest).length === 0) {
      const { [dateKey]: __, ...restLogs } = logs
      setStoredLogs(childId, restLogs)
    } else {
      setStoredLogs(childId, { ...logs, [dateKey]: rest })
    }
  } else {
    setStoredLogs(childId, { ...logs, [dateKey]: { ...day, [slotId]: entry } })
  }
  syncMealLog(
    userId,
    childId,
    dateKey,
    getDailyLog(childId, dateKey),
    getExtraSnacks(childId, dateKey),
    getHydrationLog(childId, dateKey)
  )
}

/** Get list of meal names a child has logged before (for "eaten before" suggestions). */
export function getMealHistory(childId: string): string[] {
  try {
    const raw = localStorage.getItem(k.history(childId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as string[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Add a meal name to a child's history. Deduplicated, newest first. */
export function addToMealHistory(childId: string, mealName: string): void {
  const name = mealName.trim()
  if (!name) return
  const list = getMealHistory(childId).filter((n) => n.toLowerCase() !== name.toLowerCase())
  const next = [name, ...list].slice(0, MAX_HISTORY)
  try {
    localStorage.setItem(k.history(childId), JSON.stringify(next))
  } catch {
    // quota
  }
}

type SlotHistoryMap = Record<string, string[]>

function getStoredSlotHistory(childId: string): SlotHistoryMap {
  try {
    const raw = localStorage.getItem(k.historyBySlot(childId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as SlotHistoryMap
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function setStoredSlotHistory(childId: string, data: SlotHistoryMap): void {
  try {
    localStorage.setItem(k.historyBySlot(childId), JSON.stringify(data))
  } catch {
    // quota
  }
}

/** Get meal history for a specific slot for a child. */
export function getMealHistoryForSlot(childId: string, slotId: MealSlotId | 'extraSnack'): string[] {
  const data = getStoredSlotHistory(childId)
  const list = data[slotId] ?? []
  return Array.isArray(list) ? list : []
}

/** Add a meal name to a child's history for a specific slot type. */
export function addToMealHistoryForSlot(childId: string, slotId: MealSlotId | 'extraSnack', mealName: string): void {
  const name = mealName.trim()
  if (!name) return
  const data = getStoredSlotHistory(childId)
  const existing = Array.isArray(data[slotId]) ? data[slotId]! : []
  const deduped = existing.filter((n) => n.toLowerCase() !== name.toLowerCase())
  const next = [name, ...deduped].slice(0, MAX_HISTORY)
  data[slotId] = next
  setStoredSlotHistory(childId, data)
}

function getStoredExtraSnacks(childId: string): Record<string, ExtraSnackLog[]> {
  try {
    const raw = localStorage.getItem(k.extraSnacks(childId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, ExtraSnackLog[]>
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function setStoredExtraSnacks(childId: string, data: Record<string, ExtraSnackLog[]>): void {
  try {
    localStorage.setItem(k.extraSnacks(childId), JSON.stringify(data))
  } catch {
    // quota
  }
}

/** Get extra (non-slot) snacks for a child + date. */
export function getExtraSnacks(childId: string, dateKey: string): ExtraSnackLog[] {
  const data = getStoredExtraSnacks(childId)
  return Array.isArray(data[dateKey]) ? data[dateKey] : []
}

/** Replace extra snacks for a child + date. */
export function setExtraSnacks(
  userId: string,
  childId: string,
  dateKey: string,
  snacks: ExtraSnackLog[]
): void {
  const data = getStoredExtraSnacks(childId)
  if (snacks.length === 0) {
    const { [dateKey]: _, ...rest } = data
    setStoredExtraSnacks(childId, rest)
  } else {
    setStoredExtraSnacks(childId, { ...data, [dateKey]: snacks })
  }
  syncMealLog(userId, childId, dateKey, getDailyLog(childId, dateKey), snacks, getHydrationLog(childId, dateKey))
}

function getStoredHydration(childId: string): Record<string, HydrationLogEntry[]> {
  try {
    const raw = localStorage.getItem(k.hydration(childId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, HydrationLogEntry[]>
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function setStoredHydration(childId: string, data: Record<string, HydrationLogEntry[]>): void {
  try {
    localStorage.setItem(k.hydration(childId), JSON.stringify(data))
  } catch {
    // quota
  }
}

/** Get water/drink log for a child + date. */
export function getHydrationLog(childId: string, dateKey: string): HydrationLogEntry[] {
  const data = getStoredHydration(childId)
  return Array.isArray(data[dateKey]) ? data[dateKey] : []
}

/** Replace hydration log for a child + date. */
export function setHydrationLog(
  userId: string,
  childId: string,
  dateKey: string,
  entries: HydrationLogEntry[]
): void {
  const data = getStoredHydration(childId)
  if (entries.length === 0) {
    const { [dateKey]: _, ...rest } = data
    setStoredHydration(childId, rest)
  } else {
    setStoredHydration(childId, { ...data, [dateKey]: entries })
  }
  syncMealLog(userId, childId, dateKey, getDailyLog(childId, dateKey), getExtraSnacks(childId, dateKey), entries)
}

function getStoredTrainingFeedback(childId: string): Record<string, DailyTrainingFeedback> {
  try {
    const raw = localStorage.getItem(k.feedback(childId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, DailyTrainingFeedback>
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function setStoredTrainingFeedback(childId: string, data: Record<string, DailyTrainingFeedback>): void {
  try {
    localStorage.setItem(k.feedback(childId), JSON.stringify(data))
  } catch {
    // quota
  }
}

/** Get training feedback for a child + date (keyed by session id). */
export function getTrainingFeedback(childId: string, dateKey: string): DailyTrainingFeedback {
  const data = getStoredTrainingFeedback(childId)
  const day = data[dateKey]
  return day && typeof day === 'object' ? day : {}
}

/**
 * Remove localStorage entries older than `retentionMonths` for a child.
 * Applies to all date-keyed stores: meal logs, extra snacks, hydration,
 * training feedback, and training sessions.
 */
export function pruneOldLocalData(childId: string, retentionMonths = 24): void {
  const cutoff = new Date()
  cutoff.setMonth(cutoff.getMonth() - retentionMonths)
  const cutoffKey = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`

  const storeKeys = [
    k.logs(childId),
    k.extraSnacks(childId),
    k.hydration(childId),
    k.feedback(childId),
    `energymatch-sessions-v2-${childId}`,
  ]

  for (const storeKey of storeKeys) {
    try {
      const raw = localStorage.getItem(storeKey)
      if (!raw) continue
      const data = JSON.parse(raw) as Record<string, unknown>
      if (typeof data !== 'object' || data === null) continue
      const pruned: Record<string, unknown> = {}
      for (const [dateKey, value] of Object.entries(data)) {
        if (dateKey >= cutoffKey) pruned[dateKey] = value
      }
      localStorage.setItem(storeKey, JSON.stringify(pruned))
    } catch {
      // ignore
    }
  }
}

// ── Saved recipes ──────────────────────────────────────────────────────────

/** Get all saved recipes for a child. */
export function getSavedRecipes(childId: string): SavedRecipe[] {
  try {
    const raw = localStorage.getItem(k.recipes(childId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as SavedRecipe[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Save a new recipe for a child. Returns the created recipe. */
export function saveRecipe(
  childId: string,
  name: string,
  foodEntries: FoodLogEntry[],
  extras?: {
    servings?: number
    photoThumbnailUrl?: string
    extractedIngredients?: ExtractedIngredient[]
    mealTags?: MealTag[]
  }
): SavedRecipe {
  const recipe: SavedRecipe = {
    id: crypto.randomUUID(),
    name: name.trim(),
    foodEntries,
    createdAt: new Date().toISOString(),
    ...extras,
  }
  const existing = getSavedRecipes(childId)
  try {
    localStorage.setItem(k.recipes(childId), JSON.stringify([...existing, recipe]))
  } catch {
    // quota
  }
  return recipe
}

/** Update an existing recipe's content, preserving id and createdAt. */
export function updateRecipe(
  childId: string,
  id: string,
  name: string,
  foodEntries: FoodLogEntry[],
  extras?: {
    servings?: number
    photoThumbnailUrl?: string
    extractedIngredients?: ExtractedIngredient[]
    mealTags?: MealTag[]
  }
): SavedRecipe | null {
  const recipes = getSavedRecipes(childId)
  const existing = recipes.find((r) => r.id === id)
  if (!existing) return null
  const updated: SavedRecipe = {
    ...existing,
    name: name.trim(),
    foodEntries,
    servings: extras?.servings,
    photoThumbnailUrl: extras?.photoThumbnailUrl ?? existing.photoThumbnailUrl,
    extractedIngredients: extras?.extractedIngredients,
    mealTags: extras?.mealTags !== undefined ? (extras.mealTags.length > 0 ? extras.mealTags : undefined) : existing.mealTags,
  }
  try {
    localStorage.setItem(k.recipes(childId), JSON.stringify(recipes.map((r) => (r.id === id ? updated : r))))
  } catch {
    // quota
  }
  return updated
}

/** Delete a saved recipe by id. */
export function deleteRecipe(childId: string, id: string): void {
  const existing = getSavedRecipes(childId).filter((r) => r.id !== id)
  try {
    localStorage.setItem(k.recipes(childId), JSON.stringify(existing))
  } catch {
    // quota
  }
}

/** Update the mealTags on an existing recipe. */
export function updateRecipeTags(childId: string, recipeId: string, tags: MealTag[]): void {
  const recipes = getSavedRecipes(childId).map((r) =>
    r.id === recipeId ? { ...r, mealTags: tags.length > 0 ? tags : undefined } : r
  )
  try {
    localStorage.setItem(k.recipes(childId), JSON.stringify(recipes))
  } catch {
    // quota
  }
}

// Usage data shape: Record<slotId, Record<recipeId, count>>
type UsageData = Record<string, Record<string, number>>

function getUsageData(childId: string): UsageData {
  try {
    const raw = localStorage.getItem(k.recipeUsage(childId))
    if (!raw) return {}
    return JSON.parse(raw) as UsageData
  } catch {
    return {}
  }
}

/** Record that a recipe was used in a slot (increments its per-slot count). */
export function recordRecipeUsage(childId: string, recipeId: string, slotId: string): void {
  const data = getUsageData(childId)
  if (!data[slotId]) data[slotId] = {}
  data[slotId][recipeId] = (data[slotId][recipeId] ?? 0) + 1
  try {
    localStorage.setItem(k.recipeUsage(childId), JSON.stringify(data))
  } catch {
    // quota
  }
}

/** Returns a map of recipeId → use count for the given slot. */
export function getRecipeUsageCounts(childId: string, slotId: string): Record<string, number> {
  return getUsageData(childId)[slotId] ?? {}
}

/** Save feedback for one session for a child + date. */
export function setSessionFeedback(
  userId: string,
  childId: string,
  dateKey: string,
  sessionId: string,
  entry: TrainingFeedbackEntry
): void {
  const data = getStoredTrainingFeedback(childId)
  const day = data[dateKey] ?? {}
  const updated = { ...day, [sessionId]: entry }
  setStoredTrainingFeedback(childId, { ...data, [dateKey]: updated })
  syncFeedback(userId, childId, dateKey, updated)
}
